import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './button';
import logger from '../../utils/logger';
import { LanguageContext } from '../../contexts/LanguageContext';

const errorBoundaryLogger = logger.create('ErrorBoundary');

/**
 * ErrorBoundary - Catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the whole app
 * 
 * Uses LanguageContext.Consumer for i18n support in class component
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      errorBoundaryLogger.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    
    // Could send to error reporting service here
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  renderCompactError(t) {
    return (
      <div className="flex items-center justify-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
        <div className="text-center">
          <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-400 mb-2">{t('somethingWentWrong')}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={this.handleRetry}
            className="text-xs"
            data-testid="error-boundary-retry-compact"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            {t('retry')}
          </Button>
        </div>
      </div>
    );
  }

  renderFullPageError(t) {
    return (
      <div className="min-h-screen bg-[var(--md3-surface)] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[var(--md3-surface-container)] rounded-2xl p-8 shadow-xl text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-[var(--md3-on-surface)] mb-2" data-testid="error-boundary-title">
            {t('oopsSomethingWentWrong')}
          </h1>
          
          <p className="text-[var(--md3-outline)] mb-6" data-testid="error-boundary-message">
            {t('unexpectedErrorMessage')}
          </p>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div className="mb-6 p-4 bg-red-500/10 rounded-lg text-left overflow-auto max-h-40">
              <p className="text-xs font-mono text-red-400">
                {this.state.error.toString()}
              </p>
              {this.state.errorInfo && (
                <pre className="text-xs font-mono text-red-400/70 mt-2 whitespace-pre-wrap">
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={this.handleRetry}
              className="bg-[var(--md3-primary)] hover:bg-[var(--md3-primary)]/90"
              data-testid="error-boundary-retry"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('tryAgain') || t('retry')}
            </Button>
            <Button
              variant="outline"
              onClick={this.handleGoHome}
              className="border-[var(--md3-outline)]"
              data-testid="error-boundary-home"
            >
              <Home className="w-4 h-4 mr-2" />
              {t('goHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Use LanguageContext.Consumer to access translations in class component
      return (
        <LanguageContext.Consumer>
          {({ t }) => (
            this.props.compact 
              ? this.renderCompactError(t)
              : this.renderFullPageError(t)
          )}
        </LanguageContext.Consumer>
      );
    }

    return this.props.children;
  }
}

/**
 * withErrorBoundary - HOC to wrap components with error boundary
 */
export function withErrorBoundary(WrappedComponent, errorBoundaryProps = {}) {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

/**
 * useErrorHandler - Hook for programmatic error handling
 */
export function useErrorHandler() {
  const [error, setError] = React.useState(null);

  const handleError = React.useCallback((err) => {
    errorBoundaryLogger.error('Error handled:', err);
    setError(err);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  // Re-throw error to be caught by ErrorBoundary
  if (error) {
    throw error;
  }

  return { handleError, clearError };
}

export default ErrorBoundary;
